import { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import CatalogPage from "./pages/CatalogPage";
import SimulatorPage from "./pages/SimulatorPage";
import HistoryPage from "./pages/HistoryPage";
import AdminPage from "./pages/AdminPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

export const API = process.env.REACT_APP_BACKEND_URL;

function AuthCallback({ setUser }) {
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    // StrictMode guard: prevent double processing when React remounts
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    window.history.replaceState(null, "", window.location.pathname);

    axios.post(`${API}/api/auth/session`, { session_id: sessionId }, { withCredentials: true })
      .then(res => {
        setUser(res.data);
        toast.success("Accesso con Google effettuato");
        if (res.data.role === "admin") {
          navigate("/admin", { replace: true });
        } else {
          navigate("/catalogo", { replace: true });
        }
      })
      .catch(() => {
        toast.error("Errore durante l'accesso con Google");
        navigate("/login", { replace: true });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-950">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-stone-400">
          Accesso in corso...
        </p>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip /me check if we're on the auth callback page (hash will be processed by AuthCallback)
    if (window.location.pathname === "/auth/callback") {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
  };

  return (
    <Router>
      <Toaster position="top-center" richColors />
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-stone-950">
          <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Routes>
          <Route path="/" element={<LandingPage user={user} />} />
          <Route path="/login" element={user ? <Navigate to="/catalogo" /> : <AuthPage setUser={setUser} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
          <Route path="/catalogo" element={
            user ? <CatalogPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          } />
          <Route path="/simulatore/:wallpaperId" element={
            user ? <SimulatorPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          } />
          <Route path="/storico" element={
            user ? <HistoryPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          } />
          <Route path="/admin" element={
            user?.role === "admin" ? <AdminPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </Router>
  );
}
